/**
 * Where a search query sits inside a piece of text, for highlighting it.
 *
 * Case and diacritics are folded on both sides, so "jose" finds "José" — the
 * same comparison the search is specified to make. Folding can change a
 * character's length ("İ" lowercases to two code units), so each folded unit
 * remembers the original character it came from, and the range returned is in
 * the original text's indexes.
 */

const fold = (char: string) => char.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

export type MatchRange = { start: number; end: number };

/** First occurrence of `query` in `text`, or null when there is none. */
export const findMatchRange = (text: string, query: string): MatchRange | null => {
  const needle = [...query.trim()].map(fold).join('');
  if (!needle) return null;

  let folded = '';
  /** folded index → index in `text` of the character it came from. */
  const origin: number[] = [];
  let index = 0;
  for (const char of text) {
    const part = fold(char);
    folded += part;
    for (let i = 0; i < part.length; i += 1) origin.push(index);
    index += char.length;
  }

  const at = folded.indexOf(needle);
  if (at === -1) return null;

  // Step over the last matched character, and over any accent stored after it
  // as a separate mark (text that arrived decomposed), which folds to nothing.
  let end = origin[at + needle.length - 1];
  do {
    end += String.fromCodePoint(text.codePointAt(end)!).length;
  } while (end < text.length && fold(String.fromCodePoint(text.codePointAt(end)!)) === '');

  return { start: origin[at], end };
};
